import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Table, Button, Form, Row, Col, Modal, Spinner, InputGroup, Alert } from 'react-bootstrap';
import { getApiUrl } from '../config/api';

const ResumeMedis = () => {
  const navigate = useNavigate();

  // ── Header & Time State ──
  const [time, setTime] = useState({ date: '', clock: '' });
  const [username, setUsername] = useState('Administrator');

  // ── Form Header State (Identitas Kunjungan & Pasien) ──
  const [noNota, setNoNota] = useState('');
  const [noMedrec, setNoMedrec] = useState('');
  const [nmPasien, setNmPasien] = useState('');
  const [dokterPerujuk, setDokterPerujuk] = useState('');
  const [nmKelas, setNmKelas] = useState('');
  const [nmKlasifikasi, setNmKlasifikasi] = useState('');
  const [kelamin, setKelamin] = useState('L');
  const [usia, setUsia] = useState('');
  const [tglPulang, setTglPulang] = useState('');
  const [kdDokter, setKdDokter] = useState('');
  const [nmDokter, setNmDokter] = useState('');
  const [alamat, setAlamat] = useState('');

  // ── Form Resume Medis State ──
  const [anamnesa, setAnamnesa] = useState('');
  const [stsPenyakit, setStsPenyakit] = useState('2'); // '1' = Ya, '2' = Tidak
  const [fisik, setFisik] = useState('');
  const [diagnosa, setDiagnosa] = useState('');
  const [penunjang, setPenunjang] = useState('');
  const [terapi, setTerapi] = useState('');
  const [alasan, setAlasan] = useState('');
  const [tindakanopr, setTindakanopr] = useState('');
  const [alasanopr, setAlasanopr] = useState('');
  const [anjuran, setAnjuran] = useState('');

  // ── UI Enhancements State ──
  const [activeTab, setActiveTab] = useState('semua'); // 'semua' | 'klinis' | 'tindakan' | 'edukasi'
  const [isExistingDoc, setIsExistingDoc] = useState(false);

  // ── Status & Feedback State ──
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const [alertInfo, setAlertInfo] = useState({ show: false, variant: 'info', msg: '' });

  // ── Modal Cari Nota State ──
  const [showModalNota, setShowModalNota] = useState(false);
  const [searchNotaQuery, setSearchNotaQuery] = useState('');
  const [notaList, setNotaList] = useState([]);
  const [isLoadingNotaList, setIsLoadingNotaList] = useState(false);

  // ── Modal Cari Dokter State ──
  const [showModalDokter, setShowModalDokter] = useState(false);
  const [searchDokterQuery, setSearchDokterQuery] = useState('');
  const [dokterList, setDokterList] = useState([]);
  const [isLoadingDokterList, setIsLoadingDokterList] = useState(false);

  // ── Modal Konfirmasi Cetak ──
  const [showModalCetak, setShowModalCetak] = useState(false);

  // ── Refs untuk keyboard navigation ──
  const inputNotaRef = useRef(null);
  const inputMedrecRef = useRef(null);
  const inputAnamnesaRef = useRef(null);

  // ── Clock effect ──
  useEffect(() => {
    const savedUser = localStorage.getItem('nama_user') || localStorage.getItem('user');
    if (savedUser) setUsername(savedUser);

    const updateTime = () => {
      const now = new Date();
      const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
      setTime({
        date: now.toLocaleDateString('id-ID', options),
        clock: now.toLocaleTimeString('id-ID') + ' WIB'
      });
    };
    updateTime();
    const interval = setInterval(updateTime, 1000);
    return () => clearInterval(interval);
  }, []);

  const showAlert = (msg, variant = 'info', timeout = 4000) => {
    setAlertInfo({ show: true, variant, msg });
    if (timeout) {
      setTimeout(() => setAlertInfo({ show: false, variant: 'info', msg: '' }), timeout);
    }
  };

  // ── Reset Form ──
  const handleBersih = () => {
    setNoNota('');
    setNoMedrec('');
    setNmPasien('');
    setDokterPerujuk('');
    setNmKelas('');
    setNmKlasifikasi('');
    setKelamin('L');
    setUsia('');
    setTglPulang(new Date().toISOString().split('T')[0]);
    setKdDokter('');
    setNmDokter('');
    setAlamat('');

    setAnamnesa('');
    setStsPenyakit('2');
    setFisik('');
    setDiagnosa('');
    setPenunjang('');
    setTerapi('');
    setAlasan('');
    setTindakanopr('');
    setAlasanopr('');
    setAnjuran('');
    setIsExistingDoc(false);

    if (inputNotaRef.current) {
      inputNotaRef.current.focus();
    }
  };

  // ── Cek Nomor Nota ──
  const handleCariNota = async (notaParam = null) => {
    const targetNota = (notaParam || noNota || '').trim().toUpperCase();
    if (!targetNota) {
      showAlert('Silakan masukkan Nomor Nota terlebih dahulu!', 'warning');
      if (inputNotaRef.current) inputNotaRef.current.focus();
      return;
    }

    setIsLoading(true);
    try {
      const apiUrl = getApiUrl();
      const res = await fetch(`${apiUrl}/cek_nota_resumemedis?no_nota=${encodeURIComponent(targetNota)}`);
      const json = await res.json();

      if (json.status === 1 && json.data) {
        const d = json.data;
        setNoNota(d.no_nota || targetNota);
        setNoMedrec(d.no_medrec || '');
        setNmPasien(d.nm_pasien || '');
        setDokterPerujuk(d.dokter_perujuk || d.nm_dokter || '');
        setKdDokter(d.kd_dokter || '');
        setNmDokter(d.nm_dokter || d.dokter_perujuk || '');
        setNmKelas(d.nm_kelas || '');
        setNmKlasifikasi(d.nm_klasifikasi || '');
        setKelamin(d.kelamin || 'L');
        setUsia(d.usia || '');
        setAlamat(d.alamat || '');
        setTglPulang(d.tgl_pulang || new Date().toISOString().split('T')[0]);

        // Data Resume Medis
        setAnamnesa(d.anamnesa || '');
        setStsPenyakit(d.sts_penyakit || '2');
        setFisik(d.fisik || '');
        setDiagnosa(d.diagnosa || '');
        setPenunjang(d.penunjang || '');
        setTerapi(d.terapi || '');
        setAlasan(d.alasan || '');
        setTindakanopr(d.tindakanopr || '');
        setAlasanopr(d.alasanopr || '');
        setAnjuran(d.anjuran || '');
        setIsExistingDoc(Boolean(d.is_existing));

        if (d.is_existing) {
          showAlert(`Dokumen Resume Medis Nota [${d.no_nota}] ditemukan & dimuat!`, 'success');
        } else {
          showAlert(`Data Pasien [${d.nm_pasien}] siap. Silakan lengkapi form resume medis.`, 'info');
        }

        if (inputAnamnesaRef.current) inputAnamnesaRef.current.focus();
      } else {
        showAlert(json.msg || 'Nomor Nota tidak ditemukan pada database transaksi rawat inap.', 'danger');
        if (inputNotaRef.current) inputNotaRef.current.focus();
      }
    } catch (err) {
      showAlert(`Terjadi kesalahan komunikasi server: ${err.message}`, 'danger');
    } finally {
      setIsLoading(false);
    }
  };

  // ── Cek Nomor Rekam Medis (dtpsrs) ──
  const handleCariMedrec = async (medrecParam = null) => {
    const targetMedrec = (medrecParam || noMedrec || '').trim();
    if (!targetMedrec) {
      showAlert('Silakan masukkan Nomor Rekam Medis terlebih dahulu!', 'warning');
      if (inputMedrecRef.current) inputMedrecRef.current.focus();
      return;
    }

    setIsLoading(true);
    try {
      const apiUrl = getApiUrl();
      const res = await fetch(`${apiUrl}/cek_medrec_resumemedis?no_medrec=${encodeURIComponent(targetMedrec)}`);
      const json = await res.json();

      if (json.status === 1 && json.data) {
        const d = json.data;
        setNoNota(d.no_nota || '');
        setNoMedrec(d.no_medrec || targetMedrec);
        setNmPasien(d.nm_pasien || '');
        setDokterPerujuk(d.dokter_perujuk || '');
        setKdDokter(d.kd_dokter || '');
        setNmDokter(d.nm_dokter || '');
        setNmKelas(d.nm_kelas || '');
        setNmKlasifikasi(d.nm_klasifikasi || '');
        setKelamin(d.kelamin || 'L');
        setUsia(d.usia || '');
        setAlamat(d.alamat || '');
        setTglPulang(d.tgl_pulang || new Date().toISOString().split('T')[0]);

        // Data Resume Medis
        setAnamnesa(d.anamnesa || '');
        setStsPenyakit(d.sts_penyakit || '2');
        setFisik(d.fisik || '');
        setDiagnosa(d.diagnosa || '');
        setPenunjang(d.penunjang || '');
        setTerapi(d.terapi || '');
        setAlasan(d.alasan || '');
        setTindakanopr(d.tindakanopr || '');
        setAlasanopr(d.alasanopr || '');
        setAnjuran(d.anjuran || '');
        setIsExistingDoc(Boolean(d.is_existing));

        if (d.is_existing) {
          showAlert(`Dokumen Resume Medis Rekam Medis [${targetMedrec}] ditemukan!`, 'success');
        } else {
          showAlert(`Data Pasien Rawat Inap [${d.nm_pasien}] siap. Silakan lengkapi resume medis.`, 'info');
        }

        if (inputAnamnesaRef.current) inputAnamnesaRef.current.focus();
      } else {
        showAlert(json.msg || 'Nomor Rekam Medis tidak terdaftar pada pendaftaran Rawat Inap aktif!', 'danger');
        if (inputMedrecRef.current) inputMedrecRef.current.focus();
      }
    } catch (err) {
      showAlert(`Terjadi kesalahan komunikasi server: ${err.message}`, 'danger');
    } finally {
      setIsLoading(false);
    }
  };

  // ── Simpan / Update Resume Medis ──
  const handleSimpan = async () => {
    if (!noMedrec.trim()) {
      showAlert('Nomor Rekam Medis (no_medrec) wajib terisi sebelum menyimpan resume medis!', 'warning');
      return;
    }

    setIsSaving(true);
    try {
      const apiUrl = getApiUrl();
      const payload = {
        no_nota: noNota.trim().toUpperCase(),
        no_medrec: noMedrec.trim(),
        anamnesa,
        sts_penyakit: stsPenyakit,
        fisik,
        diagnosa,
        penunjang,
        terapi,
        alasan,
        tindakanopr,
        alasanopr,
        anjuran,
        tgl_pulang: tglPulang
      };

      const res = await fetch(`${apiUrl}/simpan_resumemedis`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const json = await res.json();

      if (json.status === 1) {
        setIsExistingDoc(true);
        showAlert(json.msg || 'Dokumen Resume Medis berhasil disimpan ke database!', 'success');
        setShowModalCetak(true);
      } else {
        showAlert(json.msg || 'Gagal menyimpan resume medis!', 'danger');
      }
    } catch (err) {
      showAlert(`Gagal menyimpan: ${err.message}`, 'danger');
    } finally {
      setIsSaving(false);
    }
  };

  // ── Cetak PDF Resume Medis ──
  const handleCetakPDF = () => {
    if (!noMedrec && !noNota) {
      showAlert('Data pasien belum terisi! Silakan cari nomor nota atau nomor rekam medis.', 'warning');
      return;
    }
    setIsPrinting(true);
    const apiUrl = getApiUrl();
    const params = new URLSearchParams({
      no_nota: noNota,
      no_medrec: noMedrec,
      nm_dokter: nmDokter,
      nm_pasien: nmPasien,
      nm_klasifikasi: nmKlasifikasi,
      usia: usia,
      kelamin: kelamin
    });

    const url = `${apiUrl}/cetak_resumemedis_pdf?${params.toString()}`;
    window.open(url, '_blank');
    setShowModalCetak(false);
    setIsPrinting(false);
  };

  // ── Buka Modal Cari Nota ──
  const handleOpenModalNota = async () => {
    setShowModalNota(true);
    setIsLoadingNotaList(true);
    try {
      const apiUrl = getApiUrl();
      const res = await fetch(`${apiUrl}/cari_nota_resumemedis?keyword=${encodeURIComponent(searchNotaQuery)}`);
      const json = await res.json();
      if (json.status === 1 && Array.isArray(json.list)) {
        setNotaList(json.list);
      } else {
        setNotaList([]);
      }
    } catch (err) {
      console.warn('Gagal memuat list nota:', err);
      setNotaList([]);
    } finally {
      setIsLoadingNotaList(false);
    }
  };

  const handleSearchNotaList = async (kw) => {
    setSearchNotaQuery(kw);
    setIsLoadingNotaList(true);
    try {
      const apiUrl = getApiUrl();
      const res = await fetch(`${apiUrl}/cari_nota_resumemedis?keyword=${encodeURIComponent(kw)}`);
      const json = await res.json();
      if (json.status === 1 && Array.isArray(json.list)) {
        setNotaList(json.list);
      }
    } catch (err) {
      console.warn('Gagal mencari nota:', err);
    } finally {
      setIsLoadingNotaList(false);
    }
  };

  // ── Buka Modal Cari Dokter ──
  const handleOpenModalDokter = async () => {
    setShowModalDokter(true);
    setIsLoadingDokterList(true);
    try {
      const apiUrl = getApiUrl();
      const res = await fetch(`${apiUrl}/show_dokter`);
      const json = await res.json();
      if (json && Array.isArray(json.list_dokter)) {
        setDokterList(json.list_dokter);
      } else if (Array.isArray(json)) {
        setDokterList(json);
      } else {
        setDokterList([]);
      }
    } catch (err) {
      console.warn('Gagal memuat list dokter:', err);
      setDokterList([]);
    } finally {
      setIsLoadingDokterList(false);
    }
  };

  const filteredDokterList = dokterList.filter(d => {
    const q = searchDokterQuery.toLowerCase();
    const nama = (d.nama_dok || d.nama || '').toLowerCase();
    const nodok = (d.nodok || d.kode || '').toLowerCase();
    const spesialis = (d.specialis || '').toLowerCase();
    return nama.includes(q) || nodok.includes(q) || spesialis.includes(q);
  });

  // Hitung completion percentage
  const totalFields = 9;
  let filledCount = 0;
  if (anamnesa.trim()) filledCount++;
  if (fisik.trim()) filledCount++;
  if (diagnosa.trim()) filledCount++;
  if (penunjang.trim()) filledCount++;
  if (terapi.trim()) filledCount++;
  if (alasan.trim()) filledCount++;
  if (tindakanopr.trim()) filledCount++;
  if (alasanopr.trim()) filledCount++;
  if (anjuran.trim()) filledCount++;
  const completionPct = Math.round((filledCount / totalFields) * 100);

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', display: 'flex', flexDirection: 'column', fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" }}>
      
      {/* ══════════════════════════════════════════════════════════════
          1. HEADER UTAMA COMPACT & BERSIH
      ══════════════════════════════════════════════════════════════ */}
      <header
        style={{
          background: 'linear-gradient(135deg, #091e3a 0%, #102a4e 50%, #1a365d 100%)',
          color: '#ffffff',
          padding: '6px 16px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          boxShadow: '0 2px 8px rgba(9, 30, 58, 0.2)',
          position: 'sticky',
          top: 0,
          zIndex: 100
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: 8,
              background: 'linear-gradient(135deg, rgba(56, 189, 248, 0.25) 0%, rgba(37, 99, 235, 0.35) 100%)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <i className="bi bi-file-earmark-medical-fill text-info" style={{ fontSize: '1.1rem' }}></i>
          </div>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: '0.92rem', fontWeight: 800, letterSpacing: '0.3px', textTransform: 'uppercase' }}>
                Resume Medis Rawat Inap
              </span>
              <span
                style={{
                  fontSize: '0.62rem',
                  fontWeight: 700,
                  padding: '1px 6px',
                  borderRadius: 999,
                  background: 'rgba(56, 189, 248, 0.18)',
                  color: '#38bdf8',
                  border: '1px solid rgba(56, 189, 248, 0.35)'
                }}
              >
                EHR-03
              </span>
            </div>
            <div style={{ fontSize: '0.67rem', color: '#cbd5e1', lineHeight: 1.1 }}>
              Ringkasan Riwayat Rawat Inap &bull; Dokumen Rahasia Medis RS
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          {/* Status Dokumen Pill Compact */}
          <div
            style={{
              background: 'rgba(255, 255, 255, 0.08)',
              border: '1px solid rgba(255, 255, 255, 0.15)',
              borderRadius: 6,
              padding: '3px 10px',
              display: 'flex',
              alignItems: 'center',
              gap: 8
            }}
          >
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '0.68rem', fontWeight: 700, color: isExistingDoc ? '#34d399' : noMedrec ? '#fbbf24' : '#e2e8f0', lineHeight: 1.1 }}>
                {isExistingDoc ? (
                  <span><i className="bi bi-check-circle-fill me-1" /> Tersimpan</span>
                ) : noMedrec ? (
                  <span><i className="bi bi-pencil-fill me-1" /> Draft</span>
                ) : (
                  <span><i className="bi bi-clock-history me-1" /> Menunggu Pasien</span>
                )}
              </div>
            </div>
            {noMedrec && (
              <div style={{ borderLeft: '1px solid rgba(255,255,255,0.15)', paddingLeft: 8, fontSize: '0.72rem', fontWeight: 800, color: completionPct >= 80 ? '#34d399' : '#38bdf8' }}>
                {completionPct}%
              </div>
            )}
          </div>

          {/* User & Clock Compact */}
          <div style={{ textAlign: 'right', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#f8fafc', display: 'flex', alignItems: 'center', gap: 4 }}>
              <i className="bi bi-clock text-info" style={{ fontSize: '0.68rem' }} />
              {time.clock}
            </div>
            <div style={{ fontSize: '0.64rem', color: '#94a3b8' }}>
              Petugas: <b style={{ color: '#e2e8f0' }}>{username}</b>
            </div>
          </div>

          <Button
            variant="outline-light"
            size="sm"
            onClick={() => navigate('/home')}
            style={{
              fontWeight: 600,
              borderRadius: 6,
              fontSize: '0.7rem',
              padding: '3px 10px',
              border: '1px solid rgba(255,255,255,0.25)',
              background: 'rgba(255,255,255,0.05)'
            }}
          >
            <i className="bi bi-arrow-left me-1" /> Kembali
          </Button>
        </div>
      </header>

      {/* ══ ALERT BANNER ══ */}
      {alertInfo.show && (
        <div style={{ padding: '6px 14px 0 14px' }}>
          <Alert
            variant={alertInfo.variant}
            onClose={() => setAlertInfo({ show: false, variant: 'info', msg: '' })}
            dismissible
            className="mb-0 shadow-sm border-0"
            style={{
              fontSize: '0.74rem',
              borderRadius: 6,
              padding: '6px 12px',
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <i className={`bi bi-${alertInfo.variant === 'success' ? 'check-circle-fill text-success' : alertInfo.variant === 'danger' ? 'exclamation-octagon-fill text-danger' : alertInfo.variant === 'warning' ? 'exclamation-triangle-fill text-warning' : 'info-circle-fill text-info'} me-2`} style={{ fontSize: '0.9rem' }} />
            <div style={{ fontWeight: 500 }}>{alertInfo.msg}</div>
          </Alert>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════
          2. MAIN CONTENT AREA (COMPACT PADDING & COMPACT TYPOGRAPHY)
      ══════════════════════════════════════════════════════════════ */}
      <main style={{ flex: 1, padding: '8px 14px 56px 14px', display: 'flex', flexDirection: 'column', gap: 8 }}>
        
        {/* ── CARD 1: PANEL IDENTITAS PASIEN & KUNJUNGAN ── */}
        <Card
          className="border-0 shadow-sm"
          style={{
            borderRadius: 8,
            background: '#ffffff',
            overflow: 'hidden',
            border: '1px solid #e2e8f0'
          }}
        >
          {/* Card Header Compact */}
          <div
            style={{
              background: '#f8fafc',
              borderBottom: '1px solid #e2e8f0',
              padding: '5px 12px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <i className="bi bi-person-vcard-fill text-primary" style={{ fontSize: '0.82rem' }} />
              <span style={{ fontSize: '0.74rem', fontWeight: 800, color: '#1e293b', letterSpacing: '0.3px', textTransform: 'uppercase' }}>
                Data Kunjungan &amp; Identitas Pasien
              </span>
            </div>

            <div style={{ fontSize: '0.67rem', color: '#64748b' }}>
              Cari via <strong style={{ color: '#0f172a' }}>No. Nota</strong> atau <strong style={{ color: '#0f172a' }}>No. Medrec</strong>
            </div>
          </div>

          <Card.Body style={{ padding: '8px 12px', fontSize: '0.72rem' }}>
            <Row className="g-2">
              {/* SISI KIRI: NOTA, MEDREC, NAMA, KELAS */}
              <Col lg={6}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  
                  {/* BARIS 1: NO NOTA RESUME */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <label style={{ width: 125, fontWeight: 700, color: '#334155', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.2px', flexShrink: 0, margin: 0 }}>
                      <i className="bi bi-receipt text-primary me-1" />
                      No. Resume (Nota)
                    </label>
                    <InputGroup size="sm" style={{ flex: 1 }}>
                      <Form.Control
                        ref={inputNotaRef}
                        type="text"
                        placeholder="Ketik No Nota..."
                        value={noNota}
                        onChange={(e) => setNoNota(e.target.value.toUpperCase())}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleCariNota();
                        }}
                        style={{
                          background: '#fef9c3',
                          border: '1px solid #eab308',
                          color: '#854d0e',
                          fontWeight: 700,
                          fontSize: '0.72rem',
                          padding: '3px 8px',
                          borderRadius: '4px 0 0 4px'
                        }}
                      />
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => handleCariNota()}
                        disabled={isLoading}
                        title="Periksa Nota"
                        style={{ background: '#2563eb', border: 'none', padding: '0 10px', fontSize: '0.7rem', fontWeight: 600 }}
                      >
                        {isLoading ? <Spinner size="sm" animation="border" /> : <><i className="bi bi-search me-1" /> Cek</>}
                      </Button>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={handleOpenModalNota}
                        title="Buka Daftar Transaksi Nota"
                        style={{ borderRadius: '0 4px 4px 0', borderColor: '#cbd5e1', background: '#f8fafc', padding: '0 8px', fontSize: '0.7rem' }}
                      >
                        <i className="bi bi-list-ul" />
                      </Button>
                    </InputGroup>
                  </div>

                  {/* BARIS 2: NO MEDREC & NAMA PASIEN */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <label style={{ width: 125, fontWeight: 700, color: '#334155', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.2px', flexShrink: 0, margin: 0 }}>
                      <i className="bi bi-person-badge text-primary me-1" />
                      No. RM &amp; Nama
                    </label>
                    <div style={{ display: 'flex', flex: 1, gap: 4 }}>
                      <InputGroup size="sm" style={{ width: 125 }}>
                        <Form.Control
                          ref={inputMedrecRef}
                          type="text"
                          placeholder="No. Medrec"
                          value={noMedrec}
                          onChange={(e) => setNoMedrec(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleCariMedrec();
                          }}
                          style={{
                            fontWeight: 700,
                            fontSize: '0.72rem',
                            padding: '3px 6px',
                            background: '#ffffff',
                            textAlign: 'center',
                            border: '1px solid #cbd5e1',
                            borderRadius: '4px 0 0 4px'
                          }}
                        />
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => handleCariMedrec()}
                          title="Cari Berdasarkan Medrec"
                          style={{ borderRadius: '0 4px 4px 0', borderColor: '#cbd5e1', padding: '0 6px', fontSize: '0.7rem' }}
                        >
                          <i className="bi bi-arrow-right-short" />
                        </Button>
                      </InputGroup>
                      <Form.Control
                        size="sm"
                        type="text"
                        readOnly
                        placeholder="Nama Pasien..."
                        value={nmPasien}
                        style={{
                          flex: 1,
                          fontWeight: 700,
                          fontSize: '0.72rem',
                          padding: '3px 8px',
                          background: '#f8fafc',
                          color: '#0f172a',
                          border: '1px solid #e2e8f0',
                          borderRadius: 4
                        }}
                      />
                    </div>
                  </div>

                  {/* BARIS 3: KELAS & RUANG RAWAT */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <label style={{ width: 125, fontWeight: 700, color: '#334155', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.2px', flexShrink: 0, margin: 0 }}>
                      <i className="bi bi-hospital text-primary me-1" />
                      Kelas &amp; Klasifikasi
                    </label>
                    <div style={{ display: 'flex', flex: 1, gap: 4 }}>
                      <Form.Control
                        size="sm"
                        type="text"
                        readOnly
                        placeholder="Kelas Rawat"
                        value={nmKelas}
                        style={{ flex: 1, fontWeight: 600, fontSize: '0.72rem', padding: '3px 8px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 4 }}
                      />
                      <Form.Control
                        size="sm"
                        type="text"
                        readOnly
                        placeholder="Klasifikasi Tarif"
                        value={nmKlasifikasi}
                        style={{ flex: 1, fontWeight: 700, fontSize: '0.72rem', padding: '3px 8px', background: '#f0f9ff', color: '#0369a1', border: '1px solid #bae6fd', borderRadius: 4 }}
                      />
                    </div>
                  </div>

                </div>
              </Col>

              {/* SISI KANAN: DOKTER PERUJUK, KELAMIN/USIA/TGL PULANG, DPJP */}
              <Col lg={6}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  
                  {/* BARIS 1: DOKTER PERUJUK */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <label style={{ width: 125, fontWeight: 700, color: '#334155', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.2px', flexShrink: 0, margin: 0 }}>
                      <i className="bi bi-person-check text-primary me-1" />
                      Dokter Perujuk
                    </label>
                    <Form.Control
                      size="sm"
                      type="text"
                      placeholder="Nama Dokter Perujuk..."
                      value={dokterPerujuk}
                      onChange={(e) => setDokterPerujuk(e.target.value)}
                      style={{ flex: 1, fontWeight: 500, fontSize: '0.72rem', padding: '3px 8px', background: '#ffffff', border: '1px solid #cbd5e1', borderRadius: 4 }}
                    />
                  </div>

                  {/* BARIS 2: KELAMIN, USIA, TANGGAL PULANG */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <label style={{ width: 125, fontWeight: 700, color: '#334155', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.2px', flexShrink: 0, margin: 0 }}>
                      <i className="bi bi-calendar-event text-primary me-1" />
                      Profil Pasien
                    </label>
                    <div style={{ display: 'flex', flex: 1, gap: 4 }}>
                      <Form.Select
                        size="sm"
                        value={kelamin}
                        onChange={(e) => setKelamin(e.target.value)}
                        style={{ width: 85, fontSize: '0.7rem', padding: '3px 6px', fontWeight: 600, border: '1px solid #cbd5e1', borderRadius: 4 }}
                      >
                        <option value="L">Laki-Laki</option>
                        <option value="P">Perempuan</option>
                      </Form.Select>
                      
                      <Form.Control
                        size="sm"
                        type="text"
                        readOnly
                        placeholder="Usia"
                        value={usia}
                        style={{
                          width: 100,
                          fontWeight: 700,
                          fontSize: '0.7rem',
                          padding: '3px 4px',
                          background: '#ecfdf5',
                          textAlign: 'center',
                          color: '#065f46',
                          border: '1px solid #a7f3d0',
                          borderRadius: 4
                        }}
                        title="Usia Pasien"
                      />

                      <InputGroup size="sm" style={{ flex: 1 }}>
                        <InputGroup.Text style={{ background: '#f8fafc', borderColor: '#cbd5e1', fontSize: '0.67rem', padding: '0 6px', color: '#64748b' }}>
                          Pulang
                        </InputGroup.Text>
                        <Form.Control
                          type="text"
                          placeholder="DD/MM/YYYY"
                          value={tglPulang}
                          onChange={(e) => setTglPulang(e.target.value)}
                          style={{ fontWeight: 600, fontSize: '0.7rem', padding: '3px 6px', textAlign: 'center', border: '1px solid #cbd5e1', borderRadius: '0 4px 4px 0' }}
                          title="Tanggal Pasien Pulang (DD/MM/YYYY)"
                        />
                      </InputGroup>
                    </div>
                  </div>

                  {/* BARIS 3: DOKTER DPJP */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <label style={{ width: 125, fontWeight: 700, color: '#334155', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.2px', flexShrink: 0, margin: 0 }}>
                      <i className="bi bi-heart-pulse-fill text-danger me-1" />
                      Dokter DPJP
                    </label>
                    <div style={{ display: 'flex', flex: 1, gap: 4 }}>
                      <InputGroup size="sm" style={{ width: 95 }}>
                        <Form.Control
                          type="text"
                          placeholder="KD"
                          value={kdDokter}
                          onChange={(e) => setKdDokter(e.target.value)}
                          style={{ fontWeight: 700, fontSize: '0.7rem', padding: '3px 4px', textAlign: 'center', border: '1px solid #cbd5e1', borderRadius: '4px 0 0 4px' }}
                        />
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={handleOpenModalDokter}
                          title="Pilih Dokter DPJP"
                          style={{ borderRadius: '0 4px 4px 0', borderColor: '#cbd5e1', padding: '0 6px', fontSize: '0.7rem' }}
                        >
                          <i className="bi bi-search" />
                        </Button>
                      </InputGroup>
                      <Form.Control
                        size="sm"
                        type="text"
                        readOnly
                        placeholder="Nama Dokter DPJP..."
                        value={nmDokter}
                        style={{
                          flex: 1,
                          fontWeight: 700,
                          fontSize: '0.72rem',
                          padding: '3px 8px',
                          background: '#f8fafc',
                          color: '#1e3a8a',
                          border: '1px solid #e2e8f0',
                          borderRadius: 4
                        }}
                      />
                    </div>
                  </div>

                </div>
              </Col>
            </Row>

            {/* ALAMAT PASIEN BANNER COMPACT */}
            {alamat && (
              <div
                className="mt-2 pt-1 border-top"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  fontSize: '0.68rem',
                  color: '#475569',
                  background: '#f8fafc',
                  padding: '3px 8px',
                  borderRadius: 4
                }}
              >
                <i className="bi bi-geo-alt-fill text-danger" style={{ fontSize: '0.75rem' }} />
                <strong style={{ color: '#1e293b' }}>Alamat:</strong>
                <span>{alamat}</span>
              </div>
            )}
          </Card.Body>
        </Card>


        {/* ── CARD 2: FORM ISIAN RESUME MEDIS PASIEN (Compact Sectioned Form) ── */}
        <Card
          className="border-0 shadow-sm"
          style={{
            borderRadius: 8,
            background: '#ffffff',
            overflow: 'hidden',
            border: '1px solid #e2e8f0'
          }}
        >
          {/* Card Toolbar & Filter Tab Compact */}
          <div
            style={{
              background: '#f8fafc',
              borderBottom: '1px solid #e2e8f0',
              padding: '5px 12px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 8
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <i className="bi bi-journal-medical text-success" style={{ fontSize: '0.82rem' }} />
              <span style={{ fontSize: '0.74rem', fontWeight: 800, color: '#1e293b', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                Lembar Pengisian Resume Medis
              </span>
              <span className="text-muted ms-1" style={{ fontSize: '0.67rem' }}>
                (10 Parameter Klinis)
              </span>
            </div>

            {/* Category Filter Pills Compact */}
            <div style={{ display: 'flex', gap: 4 }}>
              {[
                { id: 'semua', label: 'Semua (10)', icon: 'bi-grid-fill' },
                { id: 'klinis', label: '1. Diagnosa & Klinis', icon: 'bi-activity' },
                { id: 'tindakan', label: '2. Terapi & Tindakan', icon: 'bi-capsule' },
                { id: 'edukasi', label: '3. Anjuran Pulang', icon: 'bi-chat-left-heart' }
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  type="button"
                  style={{
                    border: 'none',
                    background: activeTab === tab.id ? '#1e293b' : '#ffffff',
                    color: activeTab === tab.id ? '#ffffff' : '#64748b',
                    fontSize: '0.68rem',
                    fontWeight: 700,
                    padding: '3px 8px',
                    borderRadius: 4,
                    cursor: 'pointer',
                    boxShadow: activeTab === tab.id ? '0 1px 3px rgba(30, 41, 59, 0.2)' : 'inset 0 0 0 1px #e2e8f0',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4
                  }}
                >
                  <i className={`bi ${tab.icon}`} style={{ fontSize: '0.65rem' }} />
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          <Card.Body style={{ padding: '8px 12px', display: 'flex', flexDirection: 'column', gap: 8 }}>
            
            {/* ════ SEKSI 1: ANAMNESA & KONGENITAL ════ */}
            {(activeTab === 'semua' || activeTab === 'klinis') && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                <div style={{ fontSize: '0.68rem', fontWeight: 800, color: '#0369a1', textTransform: 'uppercase', letterSpacing: '0.4px', display: 'flex', alignItems: 'center', gap: 4 }}>
                  <i className="bi bi-chevron-right" style={{ fontSize: '0.65rem' }} /> A. ANAMNESA &amp; PEMERIKSAAN AWAL
                </div>

                {/* 1. ANAMNESA */}
                <div
                  style={{
                    display: 'flex',
                    border: '1px solid #e2e8f0',
                    borderRadius: 5,
                    overflow: 'hidden',
                    background: '#ffffff'
                  }}
                >
                  <div
                    style={{
                      width: 190,
                      background: '#0f2b48',
                      color: '#ffffff',
                      fontWeight: 700,
                      padding: '5px 10px',
                      fontSize: '0.69rem',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <span style={{ width: 16, height: 16, borderRadius: 3, background: 'rgba(255,255,255,0.2)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.6rem' }}>1</span>
                      <span>ANAMNESA</span>
                    </div>
                    <span style={{ fontSize: '0.62rem', color: '#93c5fd', fontWeight: 400, marginTop: 1 }}>
                      Keluhan utama &amp; riwayat sakit
                    </span>
                  </div>
                  <Form.Control
                    ref={inputAnamnesaRef}
                    as="textarea"
                    rows={2}
                    placeholder="Tuliskan keluhan utama saat masuk, riwayat penyakit sekarang, dll..."
                    value={anamnesa}
                    onChange={(e) => setAnamnesa(e.target.value)}
                    style={{
                      border: 'none',
                      borderRadius: 0,
                      fontSize: '0.72rem',
                      padding: '5px 8px',
                      lineHeight: '1.4',
                      resize: 'vertical'
                    }}
                  />
                </div>

                {/* 2. PENYAKIT BAWAAN LAHIR / KONGENITAL */}
                <div
                  style={{
                    display: 'flex',
                    border: '1px solid #e2e8f0',
                    borderRadius: 5,
                    overflow: 'hidden',
                    background: '#ffffff'
                  }}
                >
                  <div
                    style={{
                      width: 190,
                      background: '#0f2b48',
                      color: '#ffffff',
                      fontWeight: 700,
                      padding: '5px 10px',
                      fontSize: '0.69rem',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <span style={{ width: 16, height: 16, borderRadius: 3, background: 'rgba(255,255,255,0.2)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.6rem' }}>2</span>
                      <span>PENYAKIT KONGENITAL</span>
                    </div>
                    <span style={{ fontSize: '0.62rem', color: '#93c5fd', fontWeight: 400, marginTop: 1 }}>
                      Bawaan lahir (Ya / Tidak)
                    </span>
                  </div>
                  <div style={{ padding: '4px 14px', display: 'flex', alignItems: 'center', gap: 16, flex: 1, background: '#fafafa' }}>
                    <div
                      onClick={() => setStsPenyakit('1')}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                        cursor: 'pointer',
                        padding: '3px 10px',
                        borderRadius: 4,
                        background: stsPenyakit === '1' ? '#fee2e2' : '#ffffff',
                        border: `1px solid ${stsPenyakit === '1' ? '#ef4444' : '#cbd5e1'}`
                      }}
                    >
                      <Form.Check
                        type="radio"
                        id="kongenital-ya"
                        name="sts_penyakit"
                        checked={stsPenyakit === '1'}
                        onChange={() => setStsPenyakit('1')}
                        style={{ margin: 0, pointerEvents: 'none' }}
                      />
                      <label htmlFor="kongenital-ya" style={{ margin: 0, fontWeight: 800, color: stsPenyakit === '1' ? '#dc2626' : '#475569', fontSize: '0.7rem', cursor: 'pointer' }}>
                        YA (Penyakit Bawaan)
                      </label>
                    </div>

                    <div
                      onClick={() => setStsPenyakit('2')}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                        cursor: 'pointer',
                        padding: '3px 10px',
                        borderRadius: 4,
                        background: stsPenyakit === '2' ? '#dcfce7' : '#ffffff',
                        border: `1px solid ${stsPenyakit === '2' ? '#22c55e' : '#cbd5e1'}`
                      }}
                    >
                      <Form.Check
                        type="radio"
                        id="kongenital-tidak"
                        name="sts_penyakit"
                        checked={stsPenyakit === '2'}
                        onChange={() => setStsPenyakit('2')}
                        style={{ margin: 0, pointerEvents: 'none' }}
                      />
                      <label htmlFor="kongenital-tidak" style={{ margin: 0, fontWeight: 800, color: stsPenyakit === '2' ? '#15803d' : '#475569', fontSize: '0.7rem', cursor: 'pointer' }}>
                        TIDAK (Bukan Bawaan)
                      </label>
                    </div>

                    <span className="text-muted" style={{ fontSize: '0.67rem', fontStyle: 'italic' }}>
                      Pilih YA bila diagnosa berhubungan dengan kelainan kongenital.
                    </span>
                  </div>
                </div>

                {/* 3. PEMERIKSAAN FISIK & TTV */}
                <div
                  style={{
                    display: 'flex',
                    border: '1px solid #e2e8f0',
                    borderRadius: 5,
                    overflow: 'hidden',
                    background: '#ffffff'
                  }}
                >
                  <div
                    style={{
                      width: 190,
                      background: '#0f2b48',
                      color: '#ffffff',
                      fontWeight: 700,
                      padding: '5px 10px',
                      fontSize: '0.69rem',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <span style={{ width: 16, height: 16, borderRadius: 3, background: 'rgba(255,255,255,0.2)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.6rem' }}>3</span>
                      <span>PEMERIKSAAN FISIK</span>
                    </div>
                    <span style={{ fontSize: '0.62rem', color: '#93c5fd', fontWeight: 400, marginTop: 1 }}>
                      Tanda vital (TD, N, S, P, GCS)
                    </span>
                  </div>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    placeholder="Keadaan umum, kesadaran, tekanan darah, nadi, suhu, pernapasan, GCS..."
                    value={fisik}
                    onChange={(e) => setFisik(e.target.value)}
                    style={{
                      border: 'none',
                      borderRadius: 0,
                      fontSize: '0.72rem',
                      padding: '5px 8px',
                      lineHeight: '1.4',
                      resize: 'vertical'
                    }}
                  />
                </div>

                {/* 4. DIAGNOSA */}
                <div
                  style={{
                    display: 'flex',
                    border: '1px solid #e2e8f0',
                    borderRadius: 5,
                    overflow: 'hidden',
                    background: '#ffffff'
                  }}
                >
                  <div
                    style={{
                      width: 190,
                      background: '#0f2b48',
                      color: '#ffffff',
                      fontWeight: 700,
                      padding: '5px 10px',
                      fontSize: '0.69rem',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <span style={{ width: 16, height: 16, borderRadius: 3, background: 'rgba(255,255,255,0.2)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.6rem' }}>4</span>
                      <span>DIAGNOSA MEDIS</span>
                    </div>
                    <span style={{ fontSize: '0.62rem', color: '#93c5fd', fontWeight: 400, marginTop: 1 }}>
                      Diagnosa utama &amp; komplikasi
                    </span>
                  </div>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    placeholder="Diagnosa utama saat pulang, diagnosa sekunder/penyerta, ICD-10..."
                    value={diagnosa}
                    onChange={(e) => setDiagnosa(e.target.value)}
                    style={{
                      border: 'none',
                      borderRadius: 0,
                      fontSize: '0.72rem',
                      padding: '5px 8px',
                      lineHeight: '1.4',
                      fontWeight: 600,
                      color: '#0f172a',
                      resize: 'vertical'
                    }}
                  />
                </div>
              </div>
            )}

            {/* ════ SEKSI 2: TERAPI & TINDAKAN ════ */}
            {(activeTab === 'semua' || activeTab === 'tindakan') && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 5, marginTop: activeTab === 'semua' ? 3 : 0 }}>
                <div style={{ fontSize: '0.68rem', fontWeight: 800, color: '#0369a1', textTransform: 'uppercase', letterSpacing: '0.4px', display: 'flex', alignItems: 'center', gap: 4 }}>
                  <i className="bi bi-chevron-right" style={{ fontSize: '0.65rem' }} /> B. PENUNJANG, TERAPI &amp; TINDAKAN MEDIS
                </div>

                {/* 5. PEMERIKSAAN PENUNJANG */}
                <div
                  style={{
                    display: 'flex',
                    border: '1px solid #e2e8f0',
                    borderRadius: 5,
                    overflow: 'hidden',
                    background: '#ffffff'
                  }}
                >
                  <div
                    style={{
                      width: 190,
                      background: '#0f2b48',
                      color: '#ffffff',
                      fontWeight: 700,
                      padding: '5px 10px',
                      fontSize: '0.69rem',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <span style={{ width: 16, height: 16, borderRadius: 3, background: 'rgba(255,255,255,0.2)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.6rem' }}>5</span>
                      <span>PEMERIKSAAN PENUNJANG</span>
                    </div>
                    <span style={{ fontSize: '0.62rem', color: '#93c5fd', fontWeight: 400, marginTop: 1 }}>
                      Hasil lab, radiologi, EKG, USG
                    </span>
                  </div>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    placeholder="Rangkuman hasil laboratorium, radiologi / rontgen, patologi, EKG, dll..."
                    value={penunjang}
                    onChange={(e) => setPenunjang(e.target.value)}
                    style={{
                      border: 'none',
                      borderRadius: 0,
                      fontSize: '0.72rem',
                      padding: '5px 8px',
                      lineHeight: '1.4',
                      resize: 'vertical'
                    }}
                  />
                </div>

                {/* 6. TERAPI PENGOBATAN */}
                <div
                  style={{
                    display: 'flex',
                    border: '1px solid #e2e8f0',
                    borderRadius: 5,
                    overflow: 'hidden',
                    background: '#ffffff'
                  }}
                >
                  <div
                    style={{
                      width: 190,
                      background: '#0f2b48',
                      color: '#ffffff',
                      fontWeight: 700,
                      padding: '5px 10px',
                      fontSize: '0.69rem',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <span style={{ width: 16, height: 16, borderRadius: 3, background: 'rgba(255,255,255,0.2)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.6rem' }}>6</span>
                      <span>TERAPI PENGOBATAN</span>
                    </div>
                    <span style={{ fontSize: '0.62rem', color: '#93c5fd', fontWeight: 400, marginTop: 1 }}>
                      Medikasi &amp; terapi cairan rawat
                    </span>
                  </div>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    placeholder="Daftar obat-obatan, terapi injeksi, infus, dan tindakan medikasi..."
                    value={terapi}
                    onChange={(e) => setTerapi(e.target.value)}
                    style={{
                      border: 'none',
                      borderRadius: 0,
                      fontSize: '0.72rem',
                      padding: '5px 8px',
                      lineHeight: '1.4',
                      resize: 'vertical'
                    }}
                  />
                </div>

                {/* 7. ALASAN HARUS DIRAWAT INAP */}
                <div
                  style={{
                    display: 'flex',
                    border: '1px solid #e2e8f0',
                    borderRadius: 5,
                    overflow: 'hidden',
                    background: '#ffffff'
                  }}
                >
                  <div
                    style={{
                      width: 190,
                      background: '#0f2b48',
                      color: '#ffffff',
                      fontWeight: 700,
                      padding: '5px 10px',
                      fontSize: '0.69rem',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <span style={{ width: 16, height: 16, borderRadius: 3, background: 'rgba(255,255,255,0.2)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.6rem' }}>7</span>
                      <span>ALASAN RAWAT INAP</span>
                    </div>
                    <span style={{ fontSize: '0.62rem', color: '#93c5fd', fontWeight: 400, marginTop: 1 }}>
                      Indikasi medis opname
                    </span>
                  </div>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    placeholder="Alasan / indikasi klinis mengapa pasien memerlukan perawatan rawat inap..."
                    value={alasan}
                    onChange={(e) => setAlasan(e.target.value)}
                    style={{
                      border: 'none',
                      borderRadius: 0,
                      fontSize: '0.72rem',
                      padding: '5px 8px',
                      lineHeight: '1.4',
                      resize: 'vertical'
                    }}
                  />
                </div>

                {/* 8. TINDAKAN / OPERASI */}
                <div
                  style={{
                    display: 'flex',
                    border: '1px solid #e2e8f0',
                    borderRadius: 5,
                    overflow: 'hidden',
                    background: '#ffffff'
                  }}
                >
                  <div
                    style={{
                      width: 190,
                      background: '#0f2b48',
                      color: '#ffffff',
                      fontWeight: 700,
                      padding: '5px 10px',
                      fontSize: '0.69rem',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <span style={{ width: 16, height: 16, borderRadius: 3, background: 'rgba(255,255,255,0.2)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.6rem' }}>8</span>
                      <span>TINDAKAN / OPERASI</span>
                    </div>
                    <span style={{ fontSize: '0.62rem', color: '#93c5fd', fontWeight: 400, marginTop: 1 }}>
                      Nama prosedur bedah / medis
                    </span>
                  </div>
                  <Form.Control
                    as="textarea"
                    rows={1}
                    placeholder="Nama prosedur operasi / tindakan medis khusus (bila ada)..."
                    value={tindakanopr}
                    onChange={(e) => setTindakanopr(e.target.value)}
                    style={{
                      border: 'none',
                      borderRadius: 0,
                      fontSize: '0.72rem',
                      padding: '5px 8px',
                      lineHeight: '1.4'
                    }}
                  />
                </div>

                {/* 9. ALASAN OPERASI */}
                <div
                  style={{
                    display: 'flex',
                    border: '1px solid #e2e8f0',
                    borderRadius: 5,
                    overflow: 'hidden',
                    background: '#ffffff'
                  }}
                >
                  <div
                    style={{
                      width: 190,
                      background: '#0f2b48',
                      color: '#ffffff',
                      fontWeight: 700,
                      padding: '5px 10px',
                      fontSize: '0.69rem',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <span style={{ width: 16, height: 16, borderRadius: 3, background: 'rgba(255,255,255,0.2)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.6rem' }}>9</span>
                      <span>ALASAN OPERASI</span>
                    </div>
                    <span style={{ fontSize: '0.62rem', color: '#93c5fd', fontWeight: 400, marginTop: 1 }}>
                      Indikasi medis operasi
                    </span>
                  </div>
                  <Form.Control
                    as="textarea"
                    rows={1}
                    placeholder="Indikasi klinis dilakukannya tindakan operasi (bila ada)..."
                    value={alasanopr}
                    onChange={(e) => setAlasanopr(e.target.value)}
                    style={{
                      border: 'none',
                      borderRadius: 0,
                      fontSize: '0.72rem',
                      padding: '5px 8px',
                      lineHeight: '1.4'
                    }}
                  />
                </div>
              </div>
            )}

            {/* ════ SEKSI 3: ANJURAN & EDUKASI PULANG ════ */}
            {(activeTab === 'semua' || activeTab === 'edukasi') && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 5, marginTop: activeTab === 'semua' ? 3 : 0 }}>
                <div style={{ fontSize: '0.68rem', fontWeight: 800, color: '#0369a1', textTransform: 'uppercase', letterSpacing: '0.4px', display: 'flex', alignItems: 'center', gap: 4 }}>
                  <i className="bi bi-chevron-right" style={{ fontSize: '0.65rem' }} /> C. INSTRUKSI &amp; ANJURAN PASCA RAWAT
                </div>

                {/* 10. ANJURAN / SARAN */}
                <div
                  style={{
                    display: 'flex',
                    border: '1px solid #e2e8f0',
                    borderRadius: 5,
                    overflow: 'hidden',
                    background: '#ffffff'
                  }}
                >
                  <div
                    style={{
                      width: 190,
                      background: '#0f2b48',
                      color: '#ffffff',
                      fontWeight: 700,
                      padding: '5px 10px',
                      fontSize: '0.69rem',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <span style={{ width: 16, height: 16, borderRadius: 3, background: 'rgba(255,255,255,0.2)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.6rem' }}>10</span>
                      <span>ANJURAN / SARAN</span>
                    </div>
                    <span style={{ fontSize: '0.62rem', color: '#93c5fd', fontWeight: 400, marginTop: 1 }}>
                      Kontrol, obat pulang &amp; diet
                    </span>
                  </div>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    placeholder="Instruksi kontrol poliklinik, obat pulang, anjuran istirahat / pola diet, edukasi bahaya..."
                    value={anjuran}
                    onChange={(e) => setAnjuran(e.target.value)}
                    style={{
                      border: 'none',
                      borderRadius: 0,
                      fontSize: '0.72rem',
                      padding: '5px 8px',
                      lineHeight: '1.4',
                      resize: 'vertical'
                    }}
                  />
                </div>
              </div>
            )}

          </Card.Body>
        </Card>

      </main>

      {/* ══════════════════════════════════════════════════════════════
          3. FLOATING ACTION TOOLBAR FOOTER (COMPACT GLASSMORPHISM)
      ══════════════════════════════════════════════════════════════ */}
      <footer
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          background: 'rgba(15, 23, 42, 0.95)',
          backdropFilter: 'blur(8px)',
          padding: '6px 16px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          boxShadow: '0 -2px 10px rgba(0, 0, 0, 0.2)',
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
          zIndex: 99
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: '#e2e8f0', fontSize: '0.7rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: noMedrec ? '#10b981' : '#f59e0b', display: 'inline-block' }}></span>
            <span>Target: <strong style={{ color: '#ffffff' }}>{nmPasien ? `${nmPasien} (${noMedrec})` : 'Belum Memilih Pasien'}</strong></span>
          </div>
          {noNota && (
            <span style={{ color: '#94a3b8' }}>
              &bull; Nota: <code style={{ color: '#fef08a' }}>{noNota}</code>
            </span>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {/* Tombol Simpan */}
          <Button
            variant="success"
            size="sm"
            disabled={isSaving || !noMedrec}
            onClick={handleSimpan}
            style={{
              fontWeight: 700,
              fontSize: '0.72rem',
              padding: '4px 14px',
              borderRadius: 6,
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              boxShadow: '0 2px 6px rgba(34, 197, 94, 0.3)',
              border: 'none',
              background: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)'
            }}
          >
            {isSaving ? <Spinner size="sm" animation="border" /> : <i className="bi bi-floppy2-fill" />}
            SIMPAN RESUME
          </Button>

          {/* Tombol Cetak PDF */}
          <Button
            variant="info"
            size="sm"
            disabled={isPrinting || !noMedrec}
            onClick={handleCetakPDF}
            style={{
              fontWeight: 700,
              fontSize: '0.72rem',
              padding: '4px 14px',
              borderRadius: 6,
              color: '#ffffff',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              boxShadow: '0 2px 6px rgba(14, 165, 233, 0.3)',
              border: 'none',
              background: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)'
            }}
          >
            {isPrinting ? <Spinner size="sm" animation="border" /> : <i className="bi bi-printer-fill" />}
            CETAK PDF
          </Button>

          {/* Tombol Bersih */}
          <Button
            variant="outline-light"
            size="sm"
            onClick={handleBersih}
            style={{
              fontWeight: 600,
              fontSize: '0.72rem',
              padding: '4px 12px',
              borderRadius: 6,
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              borderColor: 'rgba(255,255,255,0.25)',
              background: 'rgba(255,255,255,0.08)'
            }}
          >
            <i className="bi bi-arrow-counterclockwise" />
            BERSIH
          </Button>

          {/* Tombol Keluar */}
          <Button
            variant="outline-danger"
            size="sm"
            onClick={() => navigate('/home')}
            style={{
              fontWeight: 600,
              fontSize: '0.72rem',
              padding: '4px 12px',
              borderRadius: 6,
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              borderColor: 'rgba(239, 68, 68, 0.4)',
              background: 'rgba(239, 68, 68, 0.1)',
              color: '#fca5a5'
            }}
          >
            <i className="bi bi-box-arrow-right" />
            KELUAR
          </Button>
        </div>
      </footer>

      {/* ══════════════════════════════════════════════════════════════
          4. MODAL DIALOGS (LOOKUP NOTA & DPJP)
      ══════════════════════════════════════════════════════════════ */}

      {/* ══ MODAL CARI NOTA ══ */}
      <Modal show={showModalNota} onHide={() => setShowModalNota(false)} size="lg" centered>
        <Modal.Header closeButton style={{ background: '#0f2b48', color: '#fff', padding: '8px 14px', border: 'none' }}>
          <Modal.Title style={{ fontSize: '0.84rem', fontWeight: 800, display: 'flex', alignItems: 'center', gap: 6 }}>
            <i className="bi bi-search text-info" />
            Cari Nomor Nota Transaksi Rawat Inap
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-2" style={{ background: '#f8fafc' }}>
          <InputGroup size="sm" className="mb-2 shadow-sm">
            <InputGroup.Text style={{ background: '#ffffff', borderColor: '#cbd5e1', padding: '3px 8px' }}>
              <i className="bi bi-search text-muted" style={{ fontSize: '0.75rem' }} />
            </InputGroup.Text>
            <Form.Control
              placeholder="Ketik Nomor Nota, Rekam Medis, atau Nama Pasien..."
              value={searchNotaQuery}
              onChange={(e) => handleSearchNotaList(e.target.value)}
              style={{ borderColor: '#cbd5e1', fontSize: '0.74rem', padding: '4px 8px' }}
              autoFocus
            />
          </InputGroup>

          <div style={{ maxHeight: '320px', overflowY: 'auto', borderRadius: 6, border: '1px solid #e2e8f0', background: '#ffffff' }}>
            <Table hover size="sm" style={{ fontSize: '0.7rem', margin: 0 }} className="align-middle">
              <thead style={{ background: '#1e293b', color: '#ffffff', position: 'sticky', top: 0, zIndex: 1 }}>
                <tr>
                  <th style={{ width: 35, textAlign: 'center', padding: '6px 4px' }}>#</th>
                  <th style={{ padding: '6px 8px' }}>No. Nota</th>
                  <th style={{ padding: '6px 8px' }}>No. RM</th>
                  <th style={{ padding: '6px 8px' }}>Nama Pasien</th>
                  <th style={{ padding: '6px 8px' }}>Dokter</th>
                  <th style={{ padding: '6px 8px' }}>Tgl Pulang</th>
                  <th style={{ width: 65, textAlign: 'center', padding: '6px 8px' }}>Aksi</th>
                </tr>
              </thead>
              <tbody>
                {isLoadingNotaList ? (
                  <tr>
                    <td colSpan="7" className="text-center py-3 text-primary">
                      <Spinner size="sm" animation="border" className="me-2" />
                      Mencari riwayat nota rawat inap...
                    </td>
                  </tr>
                ) : notaList.length > 0 ? (
                  notaList.map((item, idx) => (
                    <tr key={idx}>
                      <td className="text-center text-muted">{idx + 1}</td>
                      <td className="fw-bold font-monospace text-primary">{item.no_nota}</td>
                      <td className="font-monospace fw-semibold">{item.no_medrec}</td>
                      <td className="fw-semibold text-dark">{item.nm_pasien}</td>
                      <td className="text-muted">{item.nm_dokter || '-'}</td>
                      <td>{item.tgl_pulang || '-'}</td>
                      <td className="text-center">
                        <Button
                          variant="primary"
                          size="sm"
                          style={{ padding: '2px 8px', fontSize: '0.67rem', borderRadius: 4, fontWeight: 700 }}
                          onClick={() => {
                            setShowModalNota(false);
                            setNoNota(item.no_nota);
                            handleCariNota(item.no_nota);
                          }}
                        >
                          Pilih
                        </Button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" className="text-center py-3 text-muted">
                      Tidak ada data transaksi nota ditemukan. Coba ketik kata kunci lain.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </Modal.Body>
      </Modal>

      {/* ══ MODAL CARI DOKTER DPJP ══ */}
      <Modal show={showModalDokter} onHide={() => setShowModalDokter(false)} size="lg" centered>
        <Modal.Header closeButton style={{ background: '#0f2b48', color: '#fff', padding: '8px 14px', border: 'none' }}>
          <Modal.Title style={{ fontSize: '0.84rem', fontWeight: 800, display: 'flex', alignItems: 'center', gap: 6 }}>
            <i className="bi bi-person-badge-fill text-info" />
            Pilih Dokter Penanggung Jawab Pelayanan (DPJP)
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-2" style={{ background: '#f8fafc' }}>
          <InputGroup size="sm" className="mb-2 shadow-sm">
            <InputGroup.Text style={{ background: '#ffffff', borderColor: '#cbd5e1', padding: '3px 8px' }}>
              <i className="bi bi-search text-muted" style={{ fontSize: '0.75rem' }} />
            </InputGroup.Text>
            <Form.Control
              placeholder="Cari Kode, Nama Dokter, atau Spesialisasi..."
              value={searchDokterQuery}
              onChange={(e) => setSearchDokterQuery(e.target.value)}
              style={{ borderColor: '#cbd5e1', fontSize: '0.74rem', padding: '4px 8px' }}
              autoFocus
            />
          </InputGroup>

          <div style={{ maxHeight: '320px', overflowY: 'auto', borderRadius: 6, border: '1px solid #e2e8f0', background: '#ffffff' }}>
            <Table hover size="sm" style={{ fontSize: '0.7rem', margin: 0 }} className="align-middle">
              <thead style={{ background: '#1e293b', color: '#ffffff', position: 'sticky', top: 0, zIndex: 1 }}>
                <tr>
                  <th style={{ width: 35, textAlign: 'center', padding: '6px 4px' }}>#</th>
                  <th style={{ width: 75, textAlign: 'center', padding: '6px 8px' }}>Kode</th>
                  <th style={{ padding: '6px 8px' }}>Nama Dokter</th>
                  <th style={{ padding: '6px 8px' }}>Spesialisasi</th>
                  <th style={{ width: 65, textAlign: 'center', padding: '6px 8px' }}>Aksi</th>
                </tr>
              </thead>
              <tbody>
                {isLoadingDokterList ? (
                  <tr>
                    <td colSpan="5" className="text-center py-3 text-primary">
                      <Spinner size="sm" animation="border" className="me-2" />
                      Memuat master dokter...
                    </td>
                  </tr>
                ) : filteredDokterList.length > 0 ? (
                  filteredDokterList.map((item, idx) => (
                    <tr key={idx}>
                      <td className="text-center text-muted">{idx + 1}</td>
                      <td className="fw-bold font-monospace text-center text-primary">{item.nodok || item.kode}</td>
                      <td className="fw-bold text-dark">{item.nama_dok || item.nama}</td>
                      <td className="text-muted">{item.specialis || item.spesialis || '-'}</td>
                      <td className="text-center">
                        <Button
                          variant="success"
                          size="sm"
                          style={{ padding: '2px 8px', fontSize: '0.67rem', borderRadius: 4, fontWeight: 700 }}
                          onClick={() => {
                            setKdDokter(item.nodok || item.kode || '');
                            setNmDokter(item.nama_dok || item.nama || '');
                            setShowModalDokter(false);
                          }}
                        >
                          Pilih
                        </Button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="text-center py-3 text-muted">
                      Tidak ada dokter ditemukan dengan kata kunci pencarian tersebut.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </Modal.Body>
      </Modal>

      {/* ══ MODAL KONFIRMASI CETAK SETELAH SIMPAN ══ */}
      <Modal show={showModalCetak} onHide={() => setShowModalCetak(false)} centered>
        <Modal.Header closeButton style={{ background: '#0f2b48', color: '#fff', padding: '10px 16px', border: 'none' }}>
          <Modal.Title style={{ fontSize: '0.86rem', fontWeight: 800, display: 'flex', alignItems: 'center', gap: 6 }}>
            <i className="bi bi-check-circle-fill text-success" />
            Resume Medis Disimpan
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-3 text-center">
          <div
            style={{
              width: 52,
              height: 52,
              borderRadius: '50%',
              background: '#dcfce7',
              color: '#16a34a',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '1.6rem',
              marginBottom: 10
            }}
          >
            <i className="bi bi-printer-fill" />
          </div>
          <h6 style={{ fontWeight: 800, color: '#0f172a', marginBottom: 6 }}>
            Data Berhasil Tersimpan!
          </h6>
          <p className="text-muted" style={{ fontSize: '0.74rem', marginBottom: 0 }}>
            Dokumen Resume Medis untuk pasien <strong>{nmPasien}</strong> telah tersimpan ke database. Apakah Anda ingin langsung mencetak dokumen resmi PDF sekarang?
          </p>
        </Modal.Body>
        <Modal.Footer style={{ padding: '8px 14px', background: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => setShowModalCetak(false)}
            style={{ fontWeight: 600, borderRadius: 5, padding: '4px 12px', fontSize: '0.72rem' }}
          >
            Nanti Saja
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleCetakPDF}
            style={{
              fontWeight: 700,
              borderRadius: 5,
              padding: '4px 16px',
              fontSize: '0.72rem',
              background: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)',
              border: 'none'
            }}
          >
            <i className="bi bi-printer-fill me-1" /> Cetak PDF
          </Button>
        </Modal.Footer>
      </Modal>

    </div>
  );
};

export default ResumeMedis;
